import axios from 'axios';
import React, { useEffect, useMemo, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { parseISO, format } from 'date-fns';
import moment from 'moment-timezone';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import AddOutlined from '@mui/icons-material/AddOutlined';
import DeleteOutlineOutlined from '@mui/icons-material/DeleteOutlineOutlined';
import ReceiptLongOutlined from '@mui/icons-material/ReceiptLongOutlined';
import SearchOutlined from '@mui/icons-material/SearchOutlined';
import { ConfirmDialog, DataTable, DateField, EmptyState, PageHeader, StatCard } from '@routes-plan/ui';
import { useSelection } from '../../../components/context/SelectionContext';
import { Route } from '../../../models/route';
import { Office } from '../../../models/office';
import { nowOperationalDateString, operationalTimezone } from '../../../utils/operationalDateTime';
import { useResourcePermissions } from '../../../hooks/useResourcePermissions';
import { useT } from '../../../i18n';
import { setPageTitle } from '../../../utils/pageTitle';
import { formatCurrency } from '../../../utils/format/currency';
import RowAction from '../components/RowAction';
import { ScreenTourButton } from '../../../components/help/ScreenHelp';
import OfficeRouteSelects from '../components/OfficeRouteSelects';
import { movementColumns, movementTotals, type MovementRow } from '../components/movements';
import { filterGridSx, pageSx, sectionSx, statGridSx } from '../components/layout';
import { accessErrorMessage } from '../../../utils/accessErrors';

/** Valor de "todas las rutas" en la selección compartida (el API y otras pantallas ya lo usan así). */
const ALL_ROUTES = 'Seleccionar Cobro';

interface ShowAlerts {
    showAlert: (message: string, severity?: 'success' | 'info' | 'warning' | 'error') => void;
    showLoading: () => void;
    hideLoading: () => void;
}

interface ExpenseDetail {
    id: number;
    date: string;
    createdAt?: string | Date;
    description: string;
    value: number;
    balance: number;
    originCashBox: {
        id: number;
        balance: number;
        date: string;
        route: {
            id: number;
            name: string;
        } | null; // route puede ser null
    } | null; // originCashBox puede ser null
    destinationCashBox: {
        id: number;
        balance: number;
        date: string;
        route: {
            id: number;
            name: string;
        } | null; // route puede ser null
    } | null; // destinationCashBox puede ser null
    expenseType: {
        id: number;
        name: string;
    };
    operationType: {
        id: number;
        name: string;
    };
    routeName?: string;
}

const ExpenseList: React.FC<ShowAlerts> = ({ showAlert, showLoading, hideLoading }) => {
    const t = useT();
    setPageTitle('expenses.pageTitle');

    const [expensesDetail, setExpensesDetail] = useState<ExpenseDetail[]>([]);
    const { office_id: officeId, setOffice_id: setOfficeId, collect_id: routeId, setCollect_id: setRouteId } = useSelection();
    const [pendingDelete, setPendingDelete] = useState<number | null>(null);
    const { canDeleteExpenseMovement } = useResourcePermissions('/loans/expenses');
    const todayOperational = nowOperationalDateString();
    const [startDate, setStartDate] = useState<string>(todayOperational);
    const [endDate, setEndDate] = useState<string>(todayOperational);
    const [opFilter, setOpFilter] = useState<'all' | 'sum' | 'rest'>('all');

    const [officeRoutes, setOfficeRoutes] = useState<Route[]>([]);
    const [offices, setOffices] = useState<Office[]>([]);

    // Cargar sedes y datos relacionados con officeId

    useEffect(() => {
        const fetchOffice = async () => {
            try {
                showLoading();
                setExpensesDetail([]); // Limpiar los detalles si no hay routeId
                const { data } = await axios.get('offices');
                setOffices(data.data);
                await new Promise((resolve) => setTimeout(resolve, 1000));
                showAlert(t('expenses.queryOk'), 'info');
            } catch (error) {
                showAlert(t('expenses.loadOfficesError'), 'error');
            } finally {
                hideLoading();
            }
        };
        fetchOffice();
    }, []); // Solo depende de officeId

    useEffect(() => {
        // Si officeId no tiene un valor válido, no ejecutar el efecto
        if (!officeId) {
            return;
        }

        const fetchDataDetailOffice = async () => {
            showLoading();
            try {
                setExpensesDetail([]); // Limpiar los detalles si no hay routeId
                const officesResponse = axios.get('offices');
                const officeRoutesResponse = officeId ? axios.get(`offices/${officeId}/routes`) : null;
                const { data } = await axios.get(`offices/${officeId}/expenses`, {
                    params: { startDate: todayOperational, endDate: todayOperational },
                });
                setExpensesDetail(Array.isArray(data) ? data : []);

                const [officesData, officeRoutesData] = await Promise.all([
                    officesResponse,
                    officeRoutesResponse,
                ]);

                if (officesData) setOffices(officesData.data.data);
                if (officeRoutesData) setOfficeRoutes(officeRoutesData.data);

                await new Promise((resolve) => setTimeout(resolve, 1000));
            } catch (error) {
                showAlert(t('common.loadError'), 'error');
            } finally {
                hideLoading();
            }
        };

        fetchDataDetailOffice();
    }, [officeId, todayOperational]); // Solo depende de officeId
    const BoxOfficeMovemente = async () => {
        showLoading();
        try {
            setExpensesDetail([]); // Limpiar los detalles si no hay routeId
            const officesResponse = axios.get('offices');
            const officeRoutesResponse = officeId ? axios.get(`offices/${officeId}/routes`) : null;
            const { data } = await axios.get(`offices/${officeId}/expenses`, {
                params: { startDate, endDate },
            });
            setExpensesDetail(Array.isArray(data) ? data : []);

            const [officesData, officeRoutesData] = await Promise.all([
                officesResponse,
                officeRoutesResponse,
            ]);

            if (officesData) setOffices(officesData.data.data);
            if (officeRoutesData) setOfficeRoutes(officeRoutesData.data);

            await new Promise((resolve) => setTimeout(resolve, 1000));
            showAlert(t('expenses.queryOk'), 'info');

        } catch (error) {
            showAlert(t('common.loadError'), 'error');
        } finally {
            hideLoading();
        }
    };

    const formatDate = (value: string | number | undefined) => {
        if (!value) return '-';
        try {
            return format(parseISO(String(value)), 'yyyy-MM-dd HH:mm:ss');
        } catch (error) {
            return String(value);
        }
    };

    const formatDateTimeBogota = (row: MovementRow) => {
        const raw = (row as any)?.createdAt || row.date;
        if (!raw) return '-';
        const m = moment.tz(raw, operationalTimezone());
        if (m.isValid()) {
            return m.format('YYYY-MM-DD HH:mm');
        }
        return formatDate(String(raw));
    };

    const isTodayOperational = (row: MovementRow) => {
        const today = nowOperationalDateString(); // YYYY-MM-DD (Bogotá)
        const raw = (row as any)?.createdAt || row.date;
        const m = moment.tz(raw, ['YYYY-MM-DD', moment.ISO_8601], operationalTimezone());
        if (!m.isValid()) return false;
        return m.format('YYYY-MM-DD') === today;
    };

    const refreshList = async () => {
        // Esta vista depende de officeId; el filtro por ruta se hace en el front.
        if (!officeId) return;
        const { data } = await axios.get(`offices/${officeId}/expenses`, {
            params: { startDate, endDate },
        });
        setExpensesDetail(Array.isArray(data) ? data : []);
    };

    const handleDeleteMovement = (row: MovementRow) => {
        if (!canDeleteExpenseMovement) {
            return;
        }
        const id = Number(row.id);
        if (!id || !Number.isFinite(id)) {
            showAlert(t('expenses.movementNotFound'), 'warning');
            return;
        }
        if (!isTodayOperational(row)) {
            showAlert(t('expenses.onlyTodayDelete'), 'warning');
            return;
        }
        setPendingDelete(id);
    };

    const confirmDeleteMovement = async () => {
        const id = pendingDelete;
        setPendingDelete(null);
        if (id == null) return;
        showLoading();
        try {
            await axios.delete(`/cash-movements/${id}`);
            showAlert(t('expenses.movementDeleted'), 'success');
            await refreshList();
        } catch (error) {
            console.error(error);
            const msg =
                accessErrorMessage(error) ??
                ((error as any)?.response?.data?.message || t('expenses.deleteMovementError'));
            showAlert(msg, 'error');
        } finally {
            hideLoading();
        }
    };

    const rows: MovementRow[] = expensesDetail.map((expenseDetail) => ({
        id: expenseDetail.id,
        expenseName: expenseDetail.expenseType.name,
        operationTypeId: expenseDetail.operationType?.id,
        operationTypeName: expenseDetail.operationType.name,
        routeId: expenseDetail.originCashBox?.route?.id ?? expenseDetail.destinationCashBox?.route?.id ?? null,
        routeName: expenseDetail.originCashBox?.route?.name || expenseDetail.destinationCashBox?.route?.name || expenseDetail.routeName || '-',
        value: expenseDetail.value ?? 0,
        description: expenseDetail.description,
        date: expenseDetail.date,
        createdAt: (expenseDetail as any)?.createdAt || undefined,
    }));

    const filteredRows = useMemo(() => {
        let next = rows;
        // Filtro por ruta (cuando está seleccionada)
        const numericRouteId = Number(routeId);
        if (routeId && routeId !== ALL_ROUTES && Number.isFinite(numericRouteId) && numericRouteId > 0) {
            next = next.filter((r) => Number((r as any)?.routeId) === numericRouteId);
        }
        // Filtro por tipo operación
        if (opFilter !== 'all') {
            const wantTypeOpId = opFilter === 'sum' ? 1 : 2;
            next = next.filter((r) => Number((r as any)?.operationTypeId) === wantTypeOpId);
        }
        return next;
    }, [rows, routeId, opFilter]);

    // Ordenar del último al primero (fallback en front, por si el API no ordena)
    const sortedRows = useMemo(() => {
        const copy = [...filteredRows];
        copy.sort((a, b) => {
            const aRaw = (a as any)?.createdAt || a.date || '';
            const bRaw = (b as any)?.createdAt || b.date || '';
            const aT = moment.tz(aRaw, ['YYYY-MM-DD HH:mm:ss', 'YYYY-MM-DD', moment.ISO_8601], operationalTimezone()).valueOf();
            const bT = moment.tz(bRaw, ['YYYY-MM-DD HH:mm:ss', 'YYYY-MM-DD', moment.ISO_8601], operationalTimezone()).valueOf();
            if (Number.isFinite(aT) && Number.isFinite(bT) && aT !== bT) return bT - aT;
            return Number(b.id ?? 0) - Number(a.id ?? 0);
        });
        return copy;
    }, [filteredRows]);

    const fetchMovementsForRange = async (rangeStart: string, rangeEnd: string) => {
        if (!officeId) {
            showAlert(t('expenses.selectOfficeWarning'), 'warning');
            return;
        }
        showLoading();
        try {
            const { data } = await axios.get(`offices/${officeId}/expenses`, {
                params: { startDate: rangeStart, endDate: rangeEnd },
            });
            const list = Array.isArray(data) ? data : [];
            setExpensesDetail(list);
            if (list.length === 0) {
                showAlert(t('expenses.noRecordsInRange'), 'info');
            }
        } catch (error) {
            console.error(error);
            const msg =
                (error as any)?.response?.data?.message ||
                t('expenses.loadMovementsError');
            showAlert(msg, 'error');
            setExpensesDetail([]);
        } finally {
            hideLoading();
        }
    };

    const handleApplyDateRange = async () => {
        if (!startDate || !endDate) {
            showAlert(t('expenses.selectDates'), 'warning');
            return;
        }
        const startM = moment.tz(startDate, ['YYYY-MM-DD'], operationalTimezone());
        const endM = moment.tz(endDate, ['YYYY-MM-DD'], operationalTimezone());
        if (!startM.isValid() || !endM.isValid()) {
            showAlert(t('expenses.invalidRange'), 'warning');
            return;
        }
        if (endM.isBefore(startM, 'day')) {
            showAlert(t('expenses.endBeforeStart'), 'warning');
            return;
        }
        await fetchMovementsForRange(startDate, endDate);
    };

    const columns = movementColumns(t, formatDateTimeBogota);
    const totals = movementTotals(sortedRows);

    return (
        <Box sx={pageSx}>
            <PageHeader
                title={t('expenses.listTitle')}
                description={t('expenses.listDescription')}
                helpAction={<ScreenTourButton />}
                actions={
                    <Button variant="contained" startIcon={<AddOutlined />} component={NavLink} to="/loans/expenses/new" data-tour="expenses-new">
                        {t('expenses.newExpense')}
                    </Button>
                }
            />
            <Paper variant="outlined" component="form" onSubmit={(e: React.FormEvent) => { e.preventDefault(); void handleApplyDateRange(); }} sx={sectionSx}>
                <Box sx={filterGridSx}>
                    <OfficeRouteSelects
                        offices={offices}
                        officeId={officeId}
                        onOfficeChange={setOfficeId}
                        routes={officeRoutes}
                        routeId={routeId && officeRoutes.length > 0 ? routeId : ALL_ROUTES}
                        routeAllOption={{ value: ALL_ROUTES, label: t('expenses.allRoutes') }}
                        tourPrefix="expenses"
                        onRouteChange={(selectedValue) => {
                            setRouteId(selectedValue);
                            // "Todas las rutas": se vuelven a consultar los movimientos de la sede.
                            if (selectedValue === ALL_ROUTES) {
                                BoxOfficeMovemente();
                            }
                        }}
                    />
                    <DateField
                        size="small"
                        fullWidth
                        label={t('expenses.startDate')}
                        data-tour="expenses-start"
                        value={startDate}
                        onChange={(v) => setStartDate(v ?? '')}
                        max={todayOperational}
                    />
                    <DateField
                        size="small"
                        fullWidth
                        label={t('expenses.endDate')}
                        data-tour="expenses-end"
                        value={endDate}
                        onChange={(v) => setEndDate(v ?? '')}
                        min={startDate}
                        max={todayOperational}
                    />
                    <TextField
                        select
                        size="small"
                        fullWidth
                        label={t('expenses.type')}
                        data-tour="expenses-type"
                        value={opFilter}
                        onChange={(e) => setOpFilter(e.target.value as any)}
                    >
                        <MenuItem value="all">{t('common.all')}</MenuItem>
                        <MenuItem value="sum">{t('expenses.opAdd')}</MenuItem>
                        <MenuItem value="rest">{t('expenses.opSubtract')}</MenuItem>
                    </TextField>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gridColumn: '-2 / -1' }}>
                        <Button type="submit" variant="contained" startIcon={<SearchOutlined />} data-tour="expenses-query">
                            {t('common.search')}
                        </Button>
                    </Box>
                </Box>
            </Paper>
            {sortedRows.length > 0 ? (
                <Box sx={statGridSx} data-tour="expenses-stats">
                    <StatCard label={t('expenses.statMovements')} value={sortedRows.length} />
                    <StatCard label={t('expenses.statIn')} value={formatCurrency(totals.in)} />
                    <StatCard label={t('expenses.statOut')} value={formatCurrency(totals.out)} />
                </Box>
            ) : null}
            <DataTable<MovementRow>
                columns={columns}
                rows={sortedRows}
                getRowId={(row) => row.id}
                searchable
                exportFileName={t('expenses.exportFileName')}
                ariaLabel={t('expenses.listTitle')}
                tourId="expenses"
                rowActions={(row) => (
                    <RowAction
                        label={t('common.delete')}
                        icon={<DeleteOutlineOutlined fontSize="small" />}
                        color="error"
                        allowed={canDeleteExpenseMovement}
                        deniedLabel={t('expenses.noPermissionDeleteMovement')}
                        disabled={!isTodayOperational(row)}
                        disabledLabel={t('expenses.onlyTodayDelete')}
                        onClick={() => void handleDeleteMovement(row)}
                    />
                )}
                emptyState={<EmptyState dense icon={<ReceiptLongOutlined />} title={t('expenses.empty')} description={t('expenses.emptyHint')} />}
            />
            <ConfirmDialog
                open={pendingDelete != null}
                variant="danger"
                title={t('expenses.deleteTitle')}
                message={t('expenses.confirmDeleteMovement')}
                confirmLabel={t('common.delete')}
                cancelLabel={t('common.cancel')}
                onConfirm={() => void confirmDeleteMovement()}
                onCancel={() => setPendingDelete(null)}
            />
        </Box>
    );
};

export default ExpenseList;
